/**
 * ErrorLayout — единый каркас для ошибок и full-page-states
 * (404 / 500 / 403 / offline / maintenance + redirect spinner).
 *
 * Mobile: 56px sticky TopBar (back ← + logo + theme).
 * Desktop: только logo и theme в шапке, контент по центру.
 *
 * Usage:
 *   <ErrorLayout>
 *     <ErrorBlock icon="ghost" title="..." body="..." actions={...} />
 *   </ErrorLayout>
 */
import { type ReactNode } from 'react'
import { Logo } from '@/components/auth/Logo'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { Icon } from '@/components/icons/Icon'

export function ErrorLayout({ children, hideBack = false }: { children: ReactNode; hideBack?: boolean }) {
  return (
    <div className="min-h-screen bg-surface-canvas flex flex-col">
      <header className="sticky top-0 z-20 h-14 px-2 sm:px-4 flex items-center gap-1 bg-surface-canvas/85 backdrop-blur-md border-b border-border">
        {!hideBack && (
          <a
            href="/"
            aria-label="Назад"
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-border-focus/40"
          >
            <Icon name="arrow-left" size={20} />
          </a>
        )}
        <Logo size="sm" tone="brand" href="/" />
        <span className="flex-1" />
        <ThemeToggle />
      </header>
      <main className="flex-1 flex items-start lg:items-center justify-center px-6 pt-12 pb-12 lg:py-16">
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  )
}

export interface ErrorBlockProps {
  icon: string                  // Lucide icon name
  iconTone?: 'brand' | 'warning' | 'danger' | 'success' | 'muted'
  title: string
  body?: ReactNode
  actions?: ReactNode
}

const toneMap = {
  brand: 'bg-surface-brand-subtle text-accent-brand-primary',
  warning: 'bg-status-warning-bg text-status-warning-text',
  danger: 'bg-status-danger-bg text-status-danger-text',
  success: 'bg-status-success-bg text-status-success-text',
  muted: 'bg-surface-subtle text-text-secondary',
} as const

export function ErrorBlock({ icon, iconTone = 'brand', title, body, actions }: ErrorBlockProps) {
  return (
    <div className="text-center lg:text-left space-y-6">
      <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto lg:mx-0 ${toneMap[iconTone]}`}>
        <Icon name={icon} size={40} />
      </div>
      <h1 className="text-3xl font-bold font-display text-text-primary">{title}</h1>
      {body && <div className="text-text-secondary leading-relaxed">{body}</div>}
      {actions && (
        <div className="flex flex-wrap gap-3 justify-center lg:justify-start pt-2">{actions}</div>
      )}
    </div>
  )
}
